/* eslint-disable */
import {
  RACModal,
  Typography,
  RACButton,
  makeStyles,
  RACSelect,
  TextField,
  RACRadio,
  RadioGroup,
  FormControlLabel,
} from '@rentacenter/racstrap';

import React, { useState, useEffect } from 'react';
import { useUserStateContext } from '../../context/user/user-contexts';
import { logActivity, featureFlags, generateFieldVisitAnswer } from '../../api/user';
import { ReferenceKeys } from '../../types/types';
import { ActivityPayload } from '../../types/types';
import {
  WORKED_HISTORY_ACTIVITY_TYPE_CODES,
  API_ERROR_MESSAGE,
  SELECT_ONE_OPTION,
  CACHED_KEYS,
  WORKED_HISTORY_CALL_RESULT_CODES,
  STORE_NUMBER_KEY,
} from '../../constants/constants';

import { getReference } from '../../api/user';

import { Option } from '../../types/types';
import { useTranslation } from 'react-i18next';

export interface ModalProps {
  onClose: (onclose: boolean) => void;
  onSaveSuccess: () => void;
  onError: () => void;
  customerId: string;
  isFVSRetentionEnabled: boolean;
}

export const FIELD_VISIT_NOTES_MAX_LENGTH = 256;

export interface FieldVisitModalContentProps {
  callResultOptions: Option[];
  apiError: boolean;
  callResult: string;
  note: string;
  isLoading: boolean;
  isFVSRetentionEnabled: boolean;
  retentionOfferDiscussed: string;
  retentionOfferAccepted: string;

  setCallResult: (result: string) => void;
  setNote: (note: string) => void;
  setRetentionOfferDiscussed: (value: string) => void;
  setRetentionOfferAccepted: (value: string) => void;
}
const addSelectOneOption = (arrayWithOptions: any[]) => {
  if (!arrayWithOptions) {
    return [
      {
        label: SELECT_ONE_OPTION,
        value: '',
      },
    ];
  }

  return [
    {
      label: SELECT_ONE_OPTION,
      value: '',
    },
    ...arrayWithOptions,
  ];
};

export const useStyles = makeStyles((theme: any) => ({
  dialogContent: {
    marginBottom: '2rem',    
  },
  dialogRoot: {
    '& .MuiPaper-root': {
      width: theme.typography.pxToRem(500),
      maxWidth: theme.typography.pxToRem(500),
      minWidth: theme.typography.pxToRem(500),
      '!important': true,
    },
    '& .MuiDialog-paper': { 
      width: `${theme.typography.pxToRem(500)} !important`,  // Force exact width
      maxWidth: `${theme.typography.pxToRem(500)} !important`, 
      minWidth: `${theme.typography.pxToRem(500)} !important`,
    },    
    '& .MuiDialog-paperWidthSm': {
      maxWidth: theme.typography.pxToRem(500),
      width: theme.typography.pxToRem(500),
      minWidth: theme.typography.pxToRem(500),
    },
    '& .MuiDialog-paperWidthXs': {
      maxWidth: theme.typography.pxToRem(500),
      width: theme.typography.pxToRem(500),
      minWidth: theme.typography.pxToRem(500),
    },    
    '& .MuiPaper-rounded': {
      borderRadius: theme.typography.pxToRem(12),      
    },
    '& .MuiTypography-h5': {
      fontSize: theme.typography.pxToRem(20),
      fontWeight: 500,
      lineHeight: theme.typography.pxToRem(30),
    },
    '& .MuiTypography-h6': {
      fontFamily: 'OpenSans-semiBold',
      lineHeight: theme.typography.pxToRem(20),
    },
  },
  notesLabel: {
    color: '#212529',
    padding: '0px 0px 15px',
  },
  fieldVisitTextContent: {
    borderRadius: '5px',
    width: theme.typography.pxToRem(400),
  },
  callResultWrapper: {
    width: '75%',
  },
  callResultOptionsPaper: {
    maxHeight: theme.typography.pxToRem(150),
  },
  notesCharacterCount: {
    marginTop: theme.typography.pxToRem(5),
  },
  retentionQuestion: {
    marginTop: theme.typography.pxToRem(20),
    marginBottom: theme.typography.pxToRem(10),
  },
  radioGroup: {
    flexDirection: 'row',
    gap: theme.typography.pxToRem(20),
  },
}));

const FieldVisitModalContent = ({
  callResultOptions,
  apiError,
  callResult,
  note,
  isLoading,
  isFVSRetentionEnabled,
  retentionOfferDiscussed,
  retentionOfferAccepted,

  setCallResult,
  setNote,
  setRetentionOfferDiscussed,
  setRetentionOfferAccepted,
}: FieldVisitModalContentProps) => {
  const classes = useStyles();
   const{t} = useTranslation('task')

  // Check if the selected field visit result should show retention questions
  const shouldShowRetentionQuestions = () => {
    if (!isFVSRetentionEnabled || !callResult) return false;
    return true; // Always show when feature flag is ON and a result is selected
  };

  // Check if retention questions should be enabled (not disabled)
  const areRetentionQuestionsEnabled = () => {
    if (!isFVSRetentionEnabled || !callResult) return false;
    
    // Find the selected option to get its label/description
    const selectedOption = callResultOptions.find(opt => opt.value === callResult);
    const resultLabel = selectedOption?.label || '';
    
    // Check if it's one of the three specific results
    const validResults = ['Merchandise Return', 'Commitment Taken', 'Met With Customer'];
    return validResults.includes(resultLabel);
  };

  return (
    <>
      <div className={classes.callResultWrapper}>
        <RACSelect
          classes={{ paper: classes.callResultOptionsPaper }}
          inputLabel={t("Field Visit Result")}
          isDisabled={isLoading}
          defaultValue={callResult}
          loading={isLoading && !callResult.length}
          options={callResultOptions}
          onChange={(e: React.ChangeEvent<{ value: any }>) =>
            setCallResult(e.target.value)
          }
          {...(apiError && {
            errorMessage: t(API_ERROR_MESSAGE),
          })}
        />
      </div>
      
      {isFVSRetentionEnabled && (
        <>
          <div className={classes.retentionQuestion}>
            <Typography variant="h6">
              {t("Did you talk to customer about retention offers")}
            </Typography>
            <RadioGroup
              className={classes.radioGroup}
              value={retentionOfferDiscussed}
              onChange={(e) => setRetentionOfferDiscussed(e.target.value)}
            >
              <FormControlLabel
                value="1"
                control={<RACRadio value="1" disabled={!areRetentionQuestionsEnabled()} />}
                label={t("Yes")}
                disabled={!areRetentionQuestionsEnabled()}
              />
              <FormControlLabel
                value="0"
                control={<RACRadio value="0" disabled={!areRetentionQuestionsEnabled()} />}
                label={t("No")}
                disabled={!areRetentionQuestionsEnabled()}
              />
            </RadioGroup>
          </div>

          <div className={classes.retentionQuestion}>
            <Typography variant="h6">
              {t("Did the Customer Accept or Reject")}
            </Typography>
            <RadioGroup
              className={classes.radioGroup}
              value={retentionOfferAccepted}
              onChange={(e) => setRetentionOfferAccepted(e.target.value)}
            >
              <FormControlLabel
                value="1"
                control={<RACRadio value="1" disabled={!areRetentionQuestionsEnabled()} />}
                label={t("Accepted")}
                disabled={!areRetentionQuestionsEnabled()}
              />
              <FormControlLabel
                value="0"
                control={<RACRadio value="0" disabled={!areRetentionQuestionsEnabled()} />}
                label={t("Declined")}
                disabled={!areRetentionQuestionsEnabled()}
              />
            </RadioGroup>
          </div>
        </>
      )}

      <Typography variant="h6" className={classes.notesLabel}>
        {t("Notes")}
      </Typography>
      <TextField
        className={classes.fieldVisitTextContent}
        multiline
        autoFocus
        variant="outlined"
        maxRows={3}
        minRows={3}
        value={note}
        onChange={(e) => {
          if (note.length >= FIELD_VISIT_NOTES_MAX_LENGTH) {
            const trimmedNote = e.target.value?.substring(
              0,
              FIELD_VISIT_NOTES_MAX_LENGTH
            );
            setNote(trimmedNote);
          } else {
            setNote(e.target.value);
          }
        }}
      />
      <span className={classes.notesCharacterCount}>
        ({FIELD_VISIT_NOTES_MAX_LENGTH}/{note.length})
      </span>
    </>
  );
};


export const FieldVisitModal = ({
  onClose,
  onSaveSuccess,
  onError,
  customerId,
  isFVSRetentionEnabled: isFVSRetentionEnabledProp,
}: ModalProps) => {
  // const classes = useStyles();
  const other = useStyles();
  const{t} = useTranslation('task')

  const [note, setNote] = useState('');
  const { user } = useUserStateContext();
  const [pending, setPending] = useState(false);
  const [fieldVisitResultOptions, setFieldVisitResultOptions] = useState<
    Option[]
  >([]);
  const [apiError, setApiError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [fieldVisitResult, setFieldVisitResult] = useState('');
  const isFVSRetentionEnabled = isFVSRetentionEnabledProp;
  const [retentionOfferDiscussed, setRetentionOfferDiscussed] = useState('');
  const [retentionOfferAccepted, setRetentionOfferAccepted] = useState('');

  // Check if the selected field visit result should show retention questions
  const shouldShowRetentionQuestions = () => {
    if (!isFVSRetentionEnabled || !fieldVisitResult) return false;
    return true; // Always show when feature flag is ON and a result is selected
  };

  // Check if retention questions should be enabled and required
  const areRetentionQuestionsEnabled = () => {
    if (!isFVSRetentionEnabled || !fieldVisitResult) return false;
    
    // Find the selected option to get its label/description
    const selectedOption = fieldVisitResultOptions.find(opt => opt.value === fieldVisitResult);
    const resultLabel = selectedOption?.label || '';
    
    // Check if it's one of the three specific results
    const validResults = ['Merchandise Return', 'Commitment Taken', 'Met With Customer'];
    return validResults.includes(resultLabel);
  };

  // Reset retention answers when field visit result changes to non-qualifying option
  const handleFieldVisitResultChange = (value: string) => {
    setFieldVisitResult(value);
    
    // If changing to a non-qualifying result, clear retention answers
    const selectedOption = fieldVisitResultOptions.find(opt => opt.value === value);
    const resultLabel = selectedOption?.label || '';
    const validResults = ['Merchandise Return', 'Commitment Taken', 'Met With Customer'];
    
    if (!validResults.includes(resultLabel)) {
      setRetentionOfferDiscussed('');
      setRetentionOfferAccepted('');
    }
  };

  const handleSave = async () => {
    const now = new Date().toISOString();
    const custId = customerId;

    setPending(true);

    try {
      if (areRetentionQuestionsEnabled()) {
        // NEW FLOW: Feature flag ON AND valid field visit result - Call both APIs
        const activityPayload: ActivityPayload = {
          storeNumber: window.sessionStorage.getItem(STORE_NUMBER_KEY) || '',
          notes: note,
          activityDate: now,
          callResultRefCode: WORKED_HISTORY_CALL_RESULT_CODES.FIELD_VISIT,
          acctActivityRefCode: WORKED_HISTORY_ACTIVITY_TYPE_CODES.CALL_CUSTOMER,
          fieldVisitResultRefCode: fieldVisitResult,
          customerId: Number(custId),
          coWorkerId: user?.employeeId || ' ',
          callTime: now,
        };

        // Call logActivity API
        console.log('Calling logActivity with payload:', activityPayload);
        const activityResponse = await logActivity(activityPayload);
        console.log('logActivity response:', activityResponse);
        
        // Get accountManagementActivityId from response (field name is acctMgmtActivityId)
        const accountManagementActivityId = activityResponse?.data?.acctMgmtActivityId;
        console.log('accountManagementActivityId:', accountManagementActivityId);

        if (accountManagementActivityId) {
          // Call generateFieldVisitAnswer API
          const fieldVisitPayload = {
            accountManagementActivityId,
            fieldVisitResult,
            retentionOfferDiscussed: Number(retentionOfferDiscussed),
            retentionOfferAccepted: Number(retentionOfferAccepted),
          };
          console.log('Calling generateFieldVisitAnswer with payload:', fieldVisitPayload);
          await generateFieldVisitAnswer(fieldVisitPayload);
          console.log('generateFieldVisitAnswer completed');
        } else {
          console.error('accountManagementActivityId not found in response');
        }

        onSaveSuccess();
      } else {
        // OLD FLOW: Feature flag OFF - Call only logActivity
        const payload: ActivityPayload = {
          storeNumber: window.sessionStorage.getItem(STORE_NUMBER_KEY) || '',
          notes: note,
          activityDate: now,
          callResultRefCode: WORKED_HISTORY_CALL_RESULT_CODES.FIELD_VISIT,
          acctActivityRefCode: WORKED_HISTORY_ACTIVITY_TYPE_CODES.CALL_CUSTOMER,
          fieldVisitResultRefCode: fieldVisitResult,
          customerId: Number(custId),
          coWorkerId: user?.employeeId || ' ',
          callTime: now,
        };
        await logActivity(payload);
        onSaveSuccess();
      }
    } catch (error) {
      console.error('Error in handleSave:', error);
      onError();
    } finally {
      setPending(false);
    }
  };

  
function randomize(array: any) {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

const mapReferenceResponse = (references: any[]) => {
  if (!references || !references.length) return references;
  return references.map((reference) => ({
    label: reference.description,
    value: reference.referenceCode,
    ...reference,
  }));
};

// The `pipe` function takes an array of functions and applies them sequentially.
const pipe = (functions: any[]) => {
  
  // Returns a function that takes an `initialValue` and processes it through the pipeline.
  return (initialValue: any) => {

    // Log the initial value before processing.
    // console.log("Initial Value:", initialValue);

    // Uses `reduce` to apply each function in the `functions` array.
    return functions.reduce((currentValue, func) => func(currentValue), initialValue);
  };
};

const processResponse = ( input: any ) => {
  // console.log(input);
  // console.log( JSON.stringify(input?.[0]?.referenceDetails) );

  return pipe( [randomize, mapReferenceResponse, addSelectOneOption, setFieldVisitResultOptions] )(input?.[0]?.referenceDetails);
};
 

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      
      // Fetch field visit result options
      try {
        const response = await getReference(
          [ReferenceKeys.FIELD_VISIT_RESULT],
          CACHED_KEYS.FIELDVISITRESULT_CACHED_KEY
        );
        if (response) {
          processResponse(response?.data?.references);
        } else {
          setApiError(true);
        }
      } catch (ee) {
        console.log(JSON.stringify(ee));
        setApiError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <RACModal
      isOpen
      classes={{
        dialog: other.dialogRoot,
      }}
      maxWidth="xs"      
      title={t("Field Visit")}
      content={
        <FieldVisitModalContent
          callResultOptions={fieldVisitResultOptions}
          apiError={apiError}
          isLoading={isLoading}
          callResult={fieldVisitResult}
          note={note}
          isFVSRetentionEnabled={isFVSRetentionEnabled}
          retentionOfferDiscussed={retentionOfferDiscussed}
          retentionOfferAccepted={retentionOfferAccepted}
          setCallResult={handleFieldVisitResultChange}
          setNote={setNote}
          setRetentionOfferDiscussed={setRetentionOfferDiscussed}
          setRetentionOfferAccepted={setRetentionOfferAccepted}
        />
      }
      onClose={() => onClose(false)}
      buttons={
        <>
          <RACButton
            variant="outlined"
            color="secondary"
            onClick={() => onClose(false)}
            disabled={pending}
          >
            {t("Cancel")}
          </RACButton>
          <RACButton
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={
              !fieldVisitResult.length || 
              isLoading || 
              (areRetentionQuestionsEnabled() && (!retentionOfferDiscussed || !retentionOfferAccepted))
            }
            loading={pending}
          >
            {t("Save")}
          </RACButton>
        </>
      }
    />
  );
};
